/*import plugins*/
import ENDPOINTS from "@/plugins/axios/endpoints";
import moment from "moment";
import vuetify from "@/plugins/vuetify";

// Normalize 8-digit hex colors (#RRGGBBAA) → #RRGGBB
function normalizeColor(hex) {
  if (typeof hex !== "string" || !hex.startsWith("#")) return hex;

  const cleanHex = hex.trim();

  // Handle #RRGGBBAA (8-digit)
  if (/^#[0-9A-Fa-f]{8}$/.test(cleanHex)) {
    return cleanHex.slice(0, 7); // remove alpha part
  }

  // Handle #RRGGBB (6-digit)
  if (/^#[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    return cleanHex;
  }

  return hex; // return unchanged if not valid hex
}

/* ──────────────── Store ──────────────── */
const config = {
  namespaced: true,

  /*Start state*/
  state: {
    config: [],
    
    isReservationEnabled: true,
    timeSteep: 0,
    isNextBtn: true,
    serverTime: moment().format("HH:mm"),
    defaultLang: "de",
    countryIso: "de",
    salutations: [],
    themeColors: null,
    textColors: null,
    fontFamily: null,
    messages: null,
  },
  /*End state*/

  /*Start getters*/
  getters: {
    config: (state) => state.config,

    isReservationEnabled: (state) => state.isReservationEnabled,
    timeSteep: (state) => state.timeSteep,
    isNextBtn: (state) => state.isNextBtn,
    serverTime: (state) => state.serverTime,
    defaultLang: (state) => state.defaultLang,
    countryIso: (state) => state.countryIso,
    salutations: (state) => state.salutations,
    themeColors: (state) => state.themeColors || {},
    textColors: (state) => state.textColors || {},
    fontFamily: (state) => state.fontFamily,
    messages: (state) => state.messages || {},
    itemsStepTitle: (state) => state.messages?.itemsStepTitle,
    customerInfoMsg: (state) => state.messages?.customerInfoMsg,
    lastStepMsg: (state) => state.messages?.lastStepMsg,
  },
  /*End getters*/

  /*Start mutations*/
  mutations: {
    setConfig: (state, config) => {
      state.config = config;

      state.isReservationEnabled =
        config && config.reservation && config.reservation.enabled;
      state.timeSteep = config && config.calendar && config.calendar.time_steep;
      state.defaultLang = config ? config.defaultLanguage || "de" : "de";
      state.countryIso = config ? config.country_iso || "de" : "de";
    },

    isReservationEnabled: (state, val) => {
      state.isReservationEnabled = val;
    },

    setSalutations: (state, val) => {
      state.salutations = val || [];
    },

    isNextBtn: (state, val) => {
      state.isNextBtn = val;
    },

    /* Apply dynamic theme colors */
    setThemeColors(state, val) {
      if (!val || typeof val !== "object") {
        state.themeColors = null;
        return;
      }

      state.themeColors = { ...val };

      const lightTheme = vuetify.framework.theme.themes.light;

      Object.entries(val).forEach(([key, color]) => {
        const normalized = normalizeColor(color);
        if (typeof normalized === "string") {
          lightTheme[key] = normalized;
        }
      });
    },

    /* Apply dynamic text colors */
    setTextColor(state, val) {
      if (!val || typeof val !== "object") {
        state.textColors = null;
        return;
      }

      state.textColors = { ...val };

      const lightTheme = vuetify.framework.theme.themes.light;

      // Headline colors
      if (val.headlines) {
        Object.entries(val.headlines).forEach(([key, color]) => {
          const themeKey = key.startsWith("h") && !isNaN(key[1]) ? `headline${key[1]}` : key;
          const normalized = normalizeColor(color);
          if (typeof normalized === "string") {
            lightTheme[themeKey] = normalized;
          }
        });
      }

      // Product name & description
      if (val.product_name) lightTheme.product_name = normalizeColor(val.product_name);
      if (val.product_description) lightTheme.product_description = normalizeColor(val.product_description);
    },

    setFontFamily(state, val) {
      if (val && typeof val === "string") {
        state.fontFamily = val;
      } else {
        state.fontFamily = null;
      }
    },

    setMessages(state, val) {
      state.messages = typeof val === "object" && val !== null ? { ...val } : {};
    },

    interval: (state, config) => {
      var serverT = config && config.serverTime + ":00";

      var input = {
        year: moment(serverT, "DD:MM:YY HH:mm:ss").format("YY"),
        month: moment(serverT, "DD:MM:YY HH:mm:ss").format("MM"),
        day: moment(serverT, "DD:MM:YY HH:mm:ss").format("DD"),
        hours: moment(serverT, "DD:MM:YY HH:mm:ss").format("HH"),
        minutes: moment(serverT, "DD:MM:YY HH:mm:ss").format("mm"),
        seconds: moment(serverT, "DD:MM:YY HH:mm:ss").format("ss"),
      };

      var timestamp = new Date(
        input.year,
        input.month,
        input.day,
        input.hours,
        input.minutes,
        input.seconds
      );

      var interval = 1;

      setInterval(function() {
        timestamp = new Date(timestamp.getTime() + interval * 1000);
        state.serverTime =
          moment(timestamp).format("HH") + ":" + moment(timestamp).format("mm");
      }, Math.abs(interval) * 1000);
    },
  },
  /*End mutations*/

  /*Start actions*/
  actions: {
    /*Start fetch reservation-types*/
    async loadConfig({ commit }) {
      window.axios.defaults.headers.common["language"] = localStorage.getItem("lang") ? localStorage.getItem("lang") : "de";

      await window.axios
        .get(ENDPOINTS.ONLINE_SERVICES.CONFIG.GET_ALL)
        .then((response) => {
          commit("setConfig", response.data);
          commit("interval", response.data);
          commit("setSalutations", response.data.salutations);
          commit("setThemeColors", response.data.themeColors);
          commit("setTextColor", response.data.textSettings?.colors);
          commit("setFontFamily", response.data.textSettings?.font_family);
          commit("setMessages", response.data.messages);
        })
        .catch(() => {});
    },
    /*End fetch reservation-types*/
  },
  /*End actions*/
};

export default config;
